import { useState } from 'react';
import { vientDuDomaineDeContenu } from '../origines';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LE LOGO D'UNE STRUCTURE CLIENTE — LA SEULE IMAGE DE CE SITE QUI VIENNE DE
 * QUELQU'UN D'AUTRE QUE DU DÉPÔT.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Tout le reste de la surface publique est **livré avec le code** : les deux vidéos et les
 * photos générées, F-74. **Ce composant est le seul point d'entrée d'un fichier déposé par
 * une personne**, et c'est pourquoi il porte la règle plutôt que de laisser chaque page
 * poser une balise.
 *
 * `design-system-first` : « **c'est un composant, pas un attribut posé au cas par cas.** Un
 * composant d'image de contenu utilisateur porte le domaine, l'état de chargement, l'état
 * d'erreur et le repli quand le fichier a disparu. Écrit une fois, il évite autant de
 * balises dispersées portant chacune sa propre idée du domaine. »
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CE QUI EMPÊCHE UN LOGO D'EXÉCUTER DU CODE — F-70, ET LA GARANTIE EST ÉCRITE
 * AILLEURS QU'ICI.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * F-70 : « **un logo déposé NE PEUT PAS EXÉCUTER DE CODE sur aucune page qui le sert.** »
 * Quatre mécanismes la tiennent, et **aucun n'est dans ce fichier** :
 *
 * 1. **le type réel est lu dans les octets** au dépôt, jamais dans ce que l'envoi annonce ;
 * 2. **la clé est générée par le serveur**, extension dérivée du type détecté ;
 * 3. le `Content-Type` **et** le `Content-Disposition` sont **forcés à la signature** de
 *    l'URL — ce qui n'est pas une image part en pièce jointe, donc ne s'affiche pas ;
 * 4. le fichier est servi depuis **un domaine séparé, sans cookies** (D-005), donc rien de
 *    ce qui s'y exécuterait n'atteindrait l'origine de ce site.
 *
 * **Ce que ce composant ajoute est le cinquième, et il est du ressort du front** : la
 * balise est un `<img>`, jamais un `<object>`, un `<embed>` ou un `<iframe>`. **Un document
 * chargé dans un `<img>` n'exécute rien**, y compris un SVG : le navigateur le rend en mode
 * image, où script et lien externe sont inertes. C'est une propriété du mode de chargement
 * et non du fichier, et c'est pour cela qu'elle se tient ici.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **IL REFUSE UNE ADRESSE QUI NE VIENT PAS DU DOMAINE DU CONTENU, ET LE REFUS EST
 * VISIBLE.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * `design-system-first` : « une image de contenu utilisateur ne se sert **jamais** depuis le
 * domaine de l'application, ni recopiée dans le dépôt, **ni proxifiée pour simplifier** —
 * proxifier ramène le fichier dans l'origine de l'application, ce que la règle existe pour
 * empêcher. »
 *
 * **Et la règle a une seconde moitié qui vit hors de ce fichier** : « la directive d'images
 * de la politique de sécurité de contenu doit autoriser ce domaine. Une politique écrite sur
 * la seule origine de l'application casse silencieusement tous les avatars et toutes les
 * pièces jointes affichées, et **le défaut se lit dans la console du navigateur, pas dans un
 * test**. » Elle est tenue par `site/Dockerfile`, qui dérive `img-src` de **la même
 * variable** que ce fichier lit — jamais d'un littéral, sans quoi les deux divergent au
 * premier changement d'environnement.
 *
 * **`VITE_CONTENU_URL` DOIT PORTER L'ORIGINE QUI SERT RÉELLEMENT LES OBJETS, ET CE N'EST
 * PAS FORCÉMENT `contenu.swype.fr`.** L'API signe ses liens pour l'hôte de son contenant,
 * et `storage.service.ts` l'écrit : « la signature de type 4 couvre l'en-tête `Host`. Une
 * URL signée pour un hôte n'est donc pas valide sur un autre : elle **ne se réécrit pas**
 * sur `contenu.swype.fr` après coup. **`CONTENU_URL` reste posée et non lue** : la servir
 * demande un mandataire qui préserve l'en-tête `Host` devant le contenant. » Tant que ce
 * mandataire n'existe pas, **la valeur de cette variable est l'origine du contenant**, et
 * la poser sur le sous-domaine du produit ferait refuser tous les logos par ce composant
 * **et** par la politique de sécurité de contenu.
 */

/**
 * **L'origine attendue des objets servis, gravée dans le paquet au build.**
 *
 * **La lecture de la variable est ici, la RÈGLE est ailleurs**, dans
 * `shared/origines.ts`. La séparation n'est pas cosmétique : ce fichier importe
 * React et lit `import.meta`, donc **aucun script Node ne peut le charger**, et la règle
 * serait restée « exportée pour être testable » **sans test possible** — c'est la promesse
 * vide que `web/` porte encore. Le module pur, lui, est exercé avant chaque construction par
 * `scripts/verifier-les-origines.mjs`.
 */
const DOMAINE_DU_CONTENU: string | undefined = import.meta.env.VITE_CONTENU_URL;

type Props = {
  /** L'adresse présignée, telle que la route publique l'a rendue. Jamais composée ici. */
  readonly adresse: string;
  /**
   * **Le nom d'affichage de la marque, obligatoire et sans valeur par défaut.** C'est le
   * texte de remplacement du logo, et une image sans description est une image qui
   * n'existe pas pour qui ne la voit pas. Sur un carrousel de logos, c'est **le seul
   * moyen de savoir qui est nommé** quand les images ne se chargent pas.
   */
  readonly nomAffichage: string;
};

export function LogoDeMarque({ adresse, nomAffichage }: Props) {
  const [etat, poserLEtat] = useState<'charge' | 'prete' | 'perdue'>('charge');

  /**
   * **QUAND L'IMAGE NE PEUT PAS S'AFFICHER, LE NOM RESTE — ET C'EST LA RÈGLE DU
   * CARROUSEL, PAS UN REPLI DE CONFORT.**
   *
   * F-70 fait du **nom d'affichage** une colonne déclencheuse au même titre que le logo :
   * une structure qui paraît a forcément les deux. Effacer la tuile parce que l'image
   * manque retirerait du carrousel une cliente que le produit a promis de nommer, et
   * « l'accord de chaque structure pour être nommée s'obtient hors du produit ». **Le nom
   * est donc ce qui subsiste**, dans les trois cas : chargement, lien expiré, domaine
   * refusé.
   */
  const nomSeul = (
    <span className="text-16 font-weight-700 tracking-marque text-texte-faible">
      {nomAffichage}
    </span>
  );

  if (!vientDuDomaineDeContenu(adresse, DOMAINE_DU_CONTENU)) return nomSeul;
  if (etat === 'perdue') return nomSeul;

  return (
    <>
      {etat === 'charge' && (
        <span
          aria-hidden="true"
          className="h-12 w-32 animate-pulse rounded-2 bg-fond-bloc"
        />
      )}
      <img
        src={adresse}
        alt={nomAffichage}
        onLoad={() => poserLEtat('prete')}
        onError={() => poserLEtat('perdue')}
        // **`referrerPolicy` sur `no-referrer`** : sans elle, le navigateur envoie
        // l'adresse de la page au domaine du contenu. Sur cette surface-ci le gain est
        // moindre que dans l'application — l'accueil n'a pas d'identifiant dans son
        // adresse —, mais la règle est la même partout, et une exception locale est
        // exactement ce qui se recopie ensuite là où elle coûte.
        referrerPolicy="no-referrer"
        className={[
          etat === 'charge' ? 'hidden' : 'block',
          'h-12 w-auto max-w-full object-contain',
        ].join(' ')}
      />
    </>
  );
}
